import React, { useEffect, useRef } from 'react';
import { Tabs, Tab, Box, useMediaQuery, styled, Typography } from '@mui/material';
import TakeYourTestInstructions from './TakeYourTestInstructions';
import TakeYourSampleInstructions from './TakeYourSampleInstructions';
import ReturnYourSampleTestInstructions from './ReturnYourSampleTestInstructions';
import GradientCircularProgress from '../../common/GradientCircularProgress';
import useDownloadGuide from '../../../hooks/useDownloadGuide';
import PrimaryBlueButton from '../../common/buttons/PrimaryBlueButton';
import { useThemeContext } from '../../../contexts/BaseThemeContextProvider';

type InstructionStep = {
  imageUrl: string;
};

type TabContent = {
  title: string;
  steps: InstructionStep[];
  numberOfColumns: number;
};

const ScrollContainer = styled(Box)(({ theme }) => ({
  overflowY: 'hidden',
  overflowX: 'auto',
  scrollbarWidth: 'none', // Firefox
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari, Edge
  },
  msOverflowStyle: 'none', // IE
}));

const backendURL = process.env.REACT_APP_BACKEND_URL;

const HowToCollectSample = () => {
  const [activeTab, setActiveTab] = React.useState(0);
  const { theme } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const scrollableImageContainer = useRef<HTMLDivElement>(null);
  const guideDownloadUrl = `${backendURL}/files/how-to-collect-sample-guide`;
  const guideName = 'how-to-collect-sample-guide.pdf';
  const { isGuideDownloading, error, handleDownloadClick } = useDownloadGuide(guideDownloadUrl, guideName);

  const tabsContent: TabContent[] = [
    {
      title: 'How to take your test',
      numberOfColumns: 4,
      steps: [
        {
          imageUrl: '/images/how-to-collect-sample/how-to-take/step1.png',
        },
        {
          imageUrl: '/images/how-to-collect-sample/how-to-take/step2.png',
        },
        {
          imageUrl: '/images/how-to-collect-sample/how-to-take/step3.png',
        },
        {
          imageUrl: '/images/how-to-collect-sample/how-to-take/step4.png',
        },
      ],
    },
    {
      title: 'Taking your sample',
      numberOfColumns: 4,
      steps: [
        {
          imageUrl: '/images/how-to-collect-sample/taking-sample/step1.png',
        },
        {
          imageUrl: '/images/how-to-collect-sample/taking-sample/step2.png',
        },
        {
          imageUrl: '/images/how-to-collect-sample/taking-sample/step3.png',
        },
        {
          imageUrl: '/images/how-to-collect-sample/taking-sample/step4.png',
        },
      ],
    },
    {
      title: 'Return your sample',
      numberOfColumns: 2,
      steps: [
        {
          imageUrl: '/images/how-to-collect-sample/return-sample/step1.png',
        },
        {
          imageUrl: '/images/how-to-collect-sample/return-sample/step2.png',
        },
      ],
    },
  ];

  useEffect(() => {
    // Resets the scroll position when changing tabs
    // if the user scrolls to the right and then switches tabs,
    // the scroll position will be reset to the left with a smooth animation
    if (scrollableImageContainer.current) {
      scrollableImageContainer.current.scroll({
        left: 0,
        top: 0,
        behavior: 'smooth',
      });
    }

    // Only for the second tab(in the middle)
    // to scroll and center it for certain screen sizes
    if (activeTab === 1) {
      const currentActiveTabElement = document.querySelector(`[role='tab']:nth-child(${activeTab + 1})`);
      setTimeout(() => {
        currentActiveTabElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }, 500);
    }
  }, [activeTab]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const renderActiveTabTextInstructions = () => {
    return (
      <>
        <Box>{activeTab === 0 && TakeYourTestInstructions()}</Box>
        <Box>{activeTab === 1 && TakeYourSampleInstructions()}</Box>
        <Box>{activeTab === 2 && ReturnYourSampleTestInstructions()}</Box>
      </>
    );
  };

  const renderContentForTabs = () => {
    const activeTabContent = tabsContent[activeTab];
    const { numberOfColumns } = activeTabContent;
    const sxProps = {
      width: isMobile ? '280px' : `calc(${100 / numberOfColumns}% - 16px)`,
      flexShrink: 0,
    };
    const imageHeight = numberOfColumns === 2 && !isMobile ? '48%' : '100%';
    return (
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: numberOfColumns === 2 && !isMobile ? '-19.5%' : 0,
        }}
      >
        {tabsContent[activeTab].steps.map((step, index) => (
          <Box key={index} sx={sxProps}>
            <Box
              sx={{
                position: 'relative',
                paddingTop: { xs: '60%', sm: '80%' },
                mb: 2,
                overflow: 'hidden',
              }}
            >
              <Box
                component="img"
                src={step.imageUrl}
                alt={`Step ${index + 1}`}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: imageHeight,
                  objectFit: 'cover',
                  borderRadius: 3,
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const renderContentAndTabInstructions = () => {
    return (
      <Box>
        <ScrollContainer ref={scrollableImageContainer}>{renderContentForTabs()}</ScrollContainer>
        {renderActiveTabTextInstructions()}
      </Box>
    );
  };

  const renderTabsTitle = () => {
    return (
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="How to collect a good sample"
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              minWidth: isMobile ? 'auto' : '33.3%',
              color: theme.custom.pages.howTo.takeSampleCollection.tabs.titleHeader.inactive.color,
            },
            '& .Mui-selected': {
              color: `${theme.custom.pages.howTo.takeSampleCollection.tabs.titleHeader.selected.color} !important`,
            },
          }}
        >
          {tabsContent.map((tab, index) => (
            <Tab key={index} label={tab.title} />
          ))}
        </Tabs>
      </Box>
    );
  };

  const renderButtonOptions = () => {
    if (isGuideDownloading) {
      return (
        <Box sx={{ display: 'flex' }}>
          <GradientCircularProgress loadingMessage="" />
        </Box>
      );
    }
    if (error) {
      return <></>;
    }
    return (
      <Box sx={{ display: 'flex', gap: 2, mt: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
        <PrimaryBlueButton
          isLoading={false}
          onClick={handleDownloadClick}
          buttonText="Download full instructions"
          sxProps={{ marginRight: '13px', width: { xs: '100%', sm: 'auto' }, marginBottom: { xs: '10px', sm: '0' } }}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 'lg', mx: 'auto', my: 4, ...theme.custom.pages.howTo.takeSampleCollection.boxContainer }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, margin: '20px' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          How to collect a good sample
        </Typography>

        <Box
          sx={{
            backgroundColor: theme.custom.pages.howTo.takeSampleCollection.allocateSomeMinutes.backgroundColor,
            color: theme.custom.pages.howTo.takeSampleCollection.allocateSomeMinutes.color,
            padding: '15px',
            gap: '11px',
            justifyContent: 'center',
            borderRadius: '14px',
            display: 'flex',
          }}
        >
          <Box component="img" height={22} src="icons/drop-warning-collect.png" />
          Please allow 10-30 minutes to collect your sample and make sure to mail it back on the same day
        </Box>

        <Typography variant="body1" sx={{}}>
          Below you will find the latest instructions for use, along with step-by-step instructional videos.
        </Typography>

        {renderTabsTitle()}
        {renderContentAndTabInstructions()}
        {renderButtonOptions()}
      </Box>
    </Box>
  );
};

export default HowToCollectSample;
